import { useEffect, useState } from "react";
import { api, eur } from "@/lib/api";
import { toast } from "sonner";
import { Flame, Heart, EyeOff, Eye, ChevronDown, ChevronUp } from "lucide-react";

const RANGES = [7, 14, 30, 60, 90];

export default function TopProductsPanel({ slug }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const load = () => api.getMenuPerformance(slug).then(setData).catch(() => {});
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, slug]);

  const setDays = async (d) => {
    try { await api.updateTopSettings(slug, { top_days: d }); toast.success(`Classement sur ${d} jours`); load(); } catch { toast.error("Erreur"); }
  };
  const toggleHide = async (it) => {
    try {
      await api.updateTopSettings(slug, it.hidden_from_top ? { unhide_product_id: it.product_id } : { hide_product_id: it.product_id });
      toast.success(it.hidden_from_top ? `${it.name} réaffiché dans le classement` : `${it.name} masqué du classement`);
      load();
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="rounded-2xl bg-[#111F1A] border border-[#1B3028] mb-5" data-testid="top-panel">
      <button data-testid="top-panel-toggle" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between p-4 text-left">
        <span className="flex items-center gap-2 text-sm font-bold text-stone-100"><Flame className="w-4 h-4 text-orange-400" /> Les plus commandés & Coups de cœur</span>
        {open ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
      </button>
      {open && data && (
        <div className="px-4 pb-4 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-stone-400">Période du classement :</span>
            {RANGES.map((d) => (
              <button key={d} data-testid={`top-days-${d}`} onClick={() => setDays(d)} className={`text-xs px-3 py-1.5 rounded-full border ${data.days === d ? "bg-[#C69C4A] text-[#0C1713] border-[#C69C4A] font-bold" : "border-[#1B3028] text-stone-300 hover:border-[#C69C4A]/60"}`}>{d} j</button>
            ))}
            <span className="text-xs text-stone-500 ml-auto">{data.total_orders} commandes validées</span>
          </div>
          {!data.enough && <p data-testid="top-not-enough" className="text-xs text-amber-400">Pas encore assez de commandes : la catégorie « Les plus commandés » n'est pas affichée aux clients.</p>}
          <div>
            <p className="text-[11px] uppercase tracking-wider text-stone-400 font-semibold mb-2">Meilleures ventes réelles (quantités) — {data.limit} premières affichées aux clients</p>
            {data.items.length === 0 ? <p className="text-xs text-stone-500">Aucune vente sur la période.</p> : (
              <div className="space-y-1.5">
                {data.items.map((it, i) => (
                  <div key={it.product_id} data-testid={`top-row-${it.product_id}`} className={`flex items-center gap-3 rounded-xl px-3 py-2 ${it.hidden_from_top ? "bg-[#0C1713] opacity-60" : "bg-[#0F1D18]"}`}>
                    <span className="w-6 text-xs font-bold text-orange-400">{i + 1}</span>
                    <span className="flex-1 text-sm text-stone-100 truncate">{it.name} {it.is_popular && <Heart className="inline w-3 h-3 text-[#C69C4A] fill-[#C69C4A]" />} {!it.available && <span className="text-[10px] text-stone-500">(indisponible)</span>}</span>
                    <span className="text-xs text-stone-300 font-semibold">{it.qty} vendus</span>
                    <span className="text-xs text-stone-500 hidden sm:inline">{eur(it.revenue)}</span>
                    <button data-testid={`top-hide-${it.product_id}`} onClick={() => toggleHide(it)} title={it.hidden_from_top ? "Réafficher dans le classement" : "Masquer du classement"} className="text-stone-400 hover:text-[#C69C4A] p-1">
                      {it.hidden_from_top ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-stone-400 font-semibold mb-2">❤️ Coups de cœur (sélection manuelle, sans effet sur les statistiques)</p>
            {data.favorites.length === 0 ? <p className="text-xs text-stone-500">Aucun. Activez « Coup de cœur » dans la fiche d'un plat.</p> : (
              <div className="flex flex-wrap gap-2">{data.favorites.map((f) => <span key={f.id} data-testid={`fav-${f.id}`} className="text-xs rounded-full px-3 py-1 bg-[#C69C4A]/15 text-[#C69C4A] border border-[#C69C4A]/40">{f.name}</span>)}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
